import { z } from 'zod';

export const userRoleSchema = z.enum(['ADMIN', 'MANAGER', 'OPERATOR']);
export type UserRole = z.infer<typeof userRoleSchema>;

export const loginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  mfaCode: z
    .string()
    .regex(/^\d{6}$/, 'TOTP code must be 6 digits')
    .optional(),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const loginSuccessResponseSchema = z.object({
  status: z.literal('ok'),
  accessToken: z.string(),
  user: z.object({
    id: z.string(),
    email: z.string().email(),
    role: userRoleSchema,
    mfaEnabled: z.boolean(),
  }),
});

export const loginMfaRequiredResponseSchema = z.object({
  status: z.literal('mfa_required'),
});

export const loginResponseSchema = z.discriminatedUnion('status', [
  loginSuccessResponseSchema,
  loginMfaRequiredResponseSchema,
]);
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const meResponseSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  role: userRoleSchema,
  mfaEnabled: z.boolean(),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

export const refreshResponseSchema = z.object({
  accessToken: z.string(),
});
export type RefreshResponse = z.infer<typeof refreshResponseSchema>;

export const mfaEnableResponseSchema = z.object({
  secret: z.string(),
  qrCodeDataUrl: z.string(),
  otpauthUrl: z.string(),
});
export type MfaEnableResponse = z.infer<typeof mfaEnableResponseSchema>;

export const mfaVerifyRequestSchema = z.object({
  code: z.string().regex(/^\d{6}$/),
});
export type MfaVerifyRequest = z.infer<typeof mfaVerifyRequestSchema>;

export const mfaVerifySuccessResponseSchema = z.object({
  status: z.literal('ok'),
  recoveryCodes: z.array(z.string()),
});
export type MfaVerifySuccessResponse = z.infer<typeof mfaVerifySuccessResponseSchema>;

export const mfaDisableRequestSchema = z.object({
  password: z.string().min(1, 'Password richiesta per disabilitare MFA'),
});
export type MfaDisableRequest = z.infer<typeof mfaDisableRequestSchema>;

// =========================================================
// User management (admin invite flow)
// =========================================================

/**
 * Strong password policy: applied wherever a new password is SET
 * (accept-invite, future reset-password, future change-password). Login
 * itself does NOT re-validate, so legacy users with weak passwords keep
 * working until they actively change them.
 */
export const strongPasswordSchema = z
  .string()
  .min(12, 'La password deve essere di almeno 12 caratteri')
  .max(128, 'La password non può superare 128 caratteri')
  .refine((p) => /[A-Z]/.test(p), 'Deve contenere almeno una lettera maiuscola')
  .refine((p) => /[a-z]/.test(p), 'Deve contenere almeno una lettera minuscola')
  .refine((p) => /[0-9]/.test(p), 'Deve contenere almeno un numero')
  .refine((p) => /[^A-Za-z0-9]/.test(p), 'Deve contenere almeno un simbolo');

export const createUserRequestSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(120).optional(),
  role: userRoleSchema.default('OPERATOR'),
});
export type CreateUserRequest = z.infer<typeof createUserRequestSchema>;

export const updateUserRequestSchema = z.object({
  name: z.string().min(1).max(120).nullable().optional(),
  role: userRoleSchema.optional(),
  active: z.boolean().optional(),
});
export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;

export const userListItemSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string().nullable(),
  role: userRoleSchema,
  active: z.boolean(),
  mfaEnabled: z.boolean(),
  activatedAt: z.string().datetime().nullable(),
  lastLoginAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  lockedUntil: z.string().datetime().nullable(),
});
export type UserListItem = z.infer<typeof userListItemSchema>;

export const inviteCreatedResponseSchema = z.object({
  user: userListItemSchema,
  inviteExpiresAt: z.string().datetime(),
  inviteUrl: z.string().url().optional(),
});
export type InviteCreatedResponse = z.infer<typeof inviteCreatedResponseSchema>;

export const acceptInviteRequestSchema = z.object({
  token: z.string().min(32).max(128),
  password: strongPasswordSchema,
});
export type AcceptInviteRequest = z.infer<typeof acceptInviteRequestSchema>;

export const invitePeekResponseSchema = z.object({
  email: z.string().email(),
  name: z.string().nullable(),
  expiresAt: z.string().datetime(),
});
export type InvitePeekResponse = z.infer<typeof invitePeekResponseSchema>;
